import React, { useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { colors2, ButtonGhostCompact, LoaderCircleV2, SkeletonLoader } from "oolib";
import { UI_BODY, UI_BODY_SM, UI_BODY_SEMIBOLD, UI_CAPTION, UI_CAPTION_DF, UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useGetTopicClusteringTree, useNarratePriorityInsights } from "@/utils/react-query-hooks/aiAnnotation";
import { useGetDatesThatMatterFilterOptions } from "@/utils/react-query-hooks/lenses";
import { buildSignalCategoryTheme } from "@/components/analysisModules/comps/SignalFilters/signalCategories";
import { deriveInsights } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/insights";
import { buildNarrationFacts, quickHash } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/insights/facts";
import { observationTotalsByPeriod } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/scoreFindings";
import { momentumBasisText } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/format";
import { latestUsableMonthOption } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/periods";
import { resolveZoneText, resolveInsightCopy } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/copy";
import { resolveZones } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/zoneStyles";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";

/**
 * Insights — the read-out a PM opens first. One flat list, no filters:
 * the topic-clustering tree scored on the latest month with enough data,
 * rules over that map detect what changed / stands out / disagrees (see
 * PrioritiesLayout/insights), and a cached model pass phrases each one
 * (headline, why it matters, what to check) plus an opener for the period.
 * Every card links to the Priorities map (the Priorities page's field) —
 * insight here, proof there.
 *
 * While the narration is being written the cards show a skeleton, not the
 * templated wording; the template is only the fallback — for an insight the
 * BE could not validate, or when the read-out fails altogether.
 */

const Page = styled.div`
  max-width: 880px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 40px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Opener = styled.div`
  padding: 16px 18px;
  border-radius: 12px;
  background: ${colors2.white};
  border: 1px solid ${colors2.grey10};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  padding: 16px 18px;
  border-radius: 12px;
  background: ${colors2.white};
  border: 1px solid ${colors2.grey10};
`;

const Kind = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const Foot = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 4px;
`;

const MAX_INSIGHTS = 12;

// Which zone hue a kind borrows — fires-red for bad news, resolving-green
// for good, emerging-amber for "look closer", grey for the rest.
const KIND_TONE = {
  caughtFire: "negative",
  concentration: "negative",
  escalation: "warning",
  channelContrast: "warning",
  resolving: "positive",
  winSlipping: "positive",
  zoneMove: "neutral",
  goneSilent: "neutral",
  breadthDepth: "neutral",
};

const useDatasetTitleLookup = () => {
  const allTpls = useGetQueryData(["allTpls"]);
  return useMemo(() => {
    const m = {};
    (Array.isArray(allTpls) ? allTpls : []).forEach((tpl) => {
      if (tpl?.kp_content_type) m[tpl.kp_content_type] = tpl.general?.content?.title || tpl.kp_content_type;
    });
    return m;
  }, [allTpls]);
};

const evidenceText = (insight) => {
  const names = insight.subjects.slice(0, 2).map((s) => s.label);
  const more = insight.subjects.length - names.length;
  const s = insight.subject;
  const parts = [names.join(" · ") + (more > 0 ? ` +${more}` : "")];
  if (insight.subjects.length === 1) parts.push(`severity ${s.severity.value.toFixed(2)}`, `${s.n} obs · ${s.severity.docs} sources`);
  return parts.join(" · ");
};

export const InsightsTab = () => {
  const navigate = useNavigate();
  const { data: rawTree, isLoading, error } = useGetTopicClusteringTree();
  const { data: dateFilterOptions } = useGetDatesThatMatterFilterOptions();
  const titleByContentType = useDatasetTitleLookup();

  const theme = useMemo(
    () => buildSignalCategoryTheme(rawTree?.signalCategories, { prioritiesCopy: rawTree?.prioritiesCopy }),
    [rawTree]
  );
  const zoneStyles = useMemo(() => resolveZones(theme.prioritiesCopy), [theme]);
  const zoneText = useMemo(() => resolveZoneText(theme.prioritiesCopy), [theme]);
  const copy = useMemo(() => resolveInsightCopy(theme.prioritiesCopy), [theme]);

  const period = useMemo(
    () => (rawTree ? latestUsableMonthOption(dateFilterOptions?.periods, observationTotalsByPeriod(rawTree)) : null),
    [rawTree, dateFilterOptions]
  );
  const fallbackDatedDatasets = useMemo(
    () =>
      new Set(
        Object.entries(dateFilterOptions?.dateFieldMap || {})
          .filter(([, entry]) => entry?.valuePath === "kp_date_published")
          .map(([ct]) => ct)
      ),
    [dateFilterOptions]
  );

  const { insights, meta } = useMemo(
    () =>
      rawTree && period
        ? deriveInsights({
            historyTree: rawTree,
            period,
            signalMetaById: rawTree.signalMetaById || {},
            signalCategories: theme.list,
            fallbackDatedDatasets,
            prioritiesCopy: theme.prioritiesCopy,
            titleByContentType,
            displayOf: theme.displayOf,
          })
        : { insights: [], meta: null },
    [rawTree, period, theme, fallbackDatedDatasets, titleByContentType]
  );

  const shown = useMemo(() => insights.slice(0, MAX_INSIGHTS), [insights]);
  const facts = useMemo(
    () => (meta ? buildNarrationFacts({ insights: shown, meta, zones: zoneText, copy, titleByContentType, limit: MAX_INSIGHTS }) : null),
    [shown, meta, zoneText, copy, titleByContentType]
  );
  const factsKey = useMemo(() => (facts ? quickHash(JSON.stringify(facts)) : null), [facts]);
  const narrationQuery = useNarratePriorityInsights({
    facts,
    factsKey,
    periodKey: meta?.nowKey,
    enabled: shown.length > 0,
  });
  const narrated = useMemo(() => {
    const byId = new Map();
    for (const n of narrationQuery.data?.narration?.insights || []) if (n.valid) byId.set(n.id, n);
    return byId;
  }, [narrationQuery.data]);
  const opener = narrationQuery.data?.narration?.opener || null;

  const toneColor = {
    negative: zoneStyles.fires.text,
    positive: zoneStyles.resolving.text,
    warning: zoneStyles.emerging.text,
    neutral: colors2.grey60,
  };

  // The map is the Priorities page's field. (The old Topics view's
  // Priorities layout, which opened with the insight's bubbles lit, is gone;
  // the page has no per-insight selection, so this lands on the whole map.)
  const openOnMap = () => navigate("/priorities");

  if (isLoading) return <LoaderCircleV2 />;
  if (error) return <UI_BODY_SM style={{ color: colors2.grey60 }}>{SM_UIContent.SM_Dashboard.insightsError}</UI_BODY_SM>;
  if (!rawTree) {
    return (
      <UI_BODY style={{ color: colors2.grey60 }}>
        {SM_UIContent.SM_Dashboard.insightsEmpty}
      </UI_BODY>
    );
  }
  if (!period || !meta) {
    return <UI_BODY_SM style={{ color: colors2.grey60 }}>No dated observations to read insights from yet.</UI_BODY_SM>;
  }

  // Writing = the model call is in flight and nothing has come back yet.
  const writing = narrationQuery.isFetching && !narrationQuery.data;
  const status = writing
    ? "Writing the read-out…"
    : narrationQuery.isError
    ? "Read-out unavailable — showing the computed wording."
    : narrationQuery.data
    ? `Read-out ${narrationQuery.data.cached ? "from cache" : "written just now"}`
    : null;

  return (
    <Page>
      <Header>
        <div>
          <UI_BODY_SEMIBOLD style={{ color: colors2.grey80 }}>Insights · {meta.nowLabel}</UI_BODY_SEMIBOLD>
          <UI_CAPTION style={{ color: colors2.grey60, display: "block" }}>
            {insights.length} found from {meta.totalNow} observations
            {momentumBasisText(meta) ? `, ${momentumBasisText(meta)}` : ""}
            {insights.length > MAX_INSIGHTS ? ` · showing the top ${MAX_INSIGHTS}` : ""}
          </UI_CAPTION>
        </div>
        {status && <UI_CAPTION style={{ color: colors2.grey50 }}>{status}</UI_CAPTION>}
      </Header>

      {!shown.length && (
        <UI_BODY_SM style={{ color: colors2.grey60 }}>
          Nothing stands out in {meta.nowLabel} yet — no zone changes, contrasts or concentrations cleared the evidence floor.
        </UI_BODY_SM>
      )}

      {writing && shown.length > 0 && (
        <Opener>
          <SkeletonLoader style={{ height: 12, width: "92%", borderRadius: 4, marginBottom: 8 }} />
          <SkeletonLoader style={{ height: 12, width: "70%", borderRadius: 4 }} />
        </Opener>
      )}
      {!writing && opener && (
        <Opener>
          <UI_BODY style={{ color: colors2.grey80 }}>{opener}</UI_BODY>
        </Opener>
      )}

      {shown.map((insight) => {
        const n = narrated.get(insight.id);
        const color = toneColor[KIND_TONE[insight.kind]] || colors2.grey60;
        return (
          <Card key={insight.id}>
            <Kind>
              <Dot $color={color} />
              <UI_CAPTION_SEMIBOLD_DF style={{ color, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                {copy.kinds[insight.kind] || insight.kind}
              </UI_CAPTION_SEMIBOLD_DF>
            </Kind>
            {writing ? (
              // The read-out is on its way: hold the card's shape, not the
              // template's words.
              <div style={{ width: "100%" }}>
                <SkeletonLoader style={{ height: 14, width: "60%", borderRadius: 4, marginBottom: 10 }} />
                <SkeletonLoader style={{ height: 11, width: "95%", borderRadius: 4, marginBottom: 6 }} />
                <SkeletonLoader style={{ height: 11, width: "80%", borderRadius: 4, marginBottom: 10 }} />
                <SkeletonLoader style={{ height: 11, width: "50%", borderRadius: 4 }} />
              </div>
            ) : (
              <>
                <UI_BODY_SEMIBOLD style={{ color: colors2.grey80 }}>{n?.headline || insight.claim}</UI_BODY_SEMIBOLD>
                <UI_BODY_SM style={{ color: colors2.grey60 }}>{n?.why || insight.why}</UI_BODY_SM>
                {n?.check && (
                  <UI_BODY_SM style={{ color: colors2.grey80 }}>
                    <span style={{ color: colors2.grey50 }}>Check: </span>
                    {n.check}
                  </UI_BODY_SM>
                )}
              </>
            )}
            <Foot>
              <UI_CAPTION_DF style={{ color: colors2.grey40 }}>{evidenceText(insight)}</UI_CAPTION_DF>
              <ButtonGhostCompact S icon="MapTrifold" value="Show on map" onClick={openOnMap} />
            </Foot>
          </Card>
        );
      })}
    </Page>
  );
};
